import api from 'api'
import { ImpressaoHistoricoInput, ImpressaoRegistrosHistoricoInput } from 'api/ImpressaoRegistrosHistorico'
import { useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { useMap } from 'hooks/useMap'
import { useMemo } from 'react'
import { isEmpty } from 'util/checks'

import { HistoricoAtendimentoModel } from '../model/model-historico'
import { isHistoricoPrintEnable } from '../utils/utils-historico'

interface HistoricoPrintOptions {
  page: number
  enabledToPrintItemCount: number
}

const isEqual = (atendimento: HistoricoAtendimentoModel, impressao: ImpressaoHistoricoInput) =>
  impressao.atendimentoProfissionalId === atendimento.idAtendRecente &&
  impressao.historicoCabecalhoId === atendimento.idAtendProcessado &&
  impressao.cuidadoCompartilhadoEvolId === atendimento.cuidadoCompartilhadoEvolucao?.id &&
  impressao.uuidRegistro === atendimento.codigoUnicoRegistro &&
  impressao.cpfCnsCidadao === atendimento.cpfCnsCidadao

const convertToImpressao = (atendimento: HistoricoAtendimentoModel): ImpressaoHistoricoInput => ({
  atendimentoProfissionalId: atendimento.idAtendRecente,
  historicoCabecalhoId: atendimento.idAtendProcessado,
  cuidadoCompartilhadoEvolId: atendimento.cuidadoCompartilhadoEvolucao?.id,
  uuidRegistro: atendimento.codigoUnicoRegistro,
  cpfCnsCidadao: atendimento.cpfCnsCidadao,
  tipoApresentacao: atendimento.tipoApresentacao,
})

const createPage = (current: ImpressaoHistoricoInput[], value: ImpressaoHistoricoInput[]) =>
  isEmpty(current) ? value : [...current, ...value]

export const useHistoricoPrint = ({ page, enabledToPrintItemCount }: HistoricoPrintOptions) => {
  const [selectionPages, { set: setSelectionPage, remove: removeSelectionPage, reset: clearSelections }] = useMap<
    Record<number, ImpressaoHistoricoInput[]>
  >()
  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()

  return useMemo(() => {
    const isItemSelected = (atendimento: HistoricoAtendimentoModel) =>
      !!selectionPages[page]?.some((impressao) => isEqual(atendimento, impressao))

    const isAllPageItemsSelected =
      !isEmpty(selectionPages[page]) && selectionPages[page].length === enabledToPrintItemCount

    const remove = (atendimento: HistoricoAtendimentoModel) => {
      const values = selectionPages[page]?.filter((impressao) => !isEqual(atendimento, impressao))
      setSelectionPage(page, values)
    }

    const select = (atendimento: HistoricoAtendimentoModel) => {
      const input = [convertToImpressao(atendimento)]
      const newPage = createPage(selectionPages[page], input)
      setSelectionPage(page, newPage)
    }

    const handleSelectItem = (checked: boolean, atendimento: HistoricoAtendimentoModel) =>
      checked ? select(atendimento) : remove(atendimento)

    const selectPage = (historico: HistoricoAtendimentoModel[]) => {
      const actualPage = historico
        ?.filter((atendimento) => isHistoricoPrintEnable(atendimento) && !isItemSelected(atendimento))
        ?.map((atendimento) => convertToImpressao(atendimento))

      const newPage = createPage(selectionPages[page], actualPage)
      setSelectionPage(page, newPage)
    }

    const handleSelectPage = (checked: boolean, historico: HistoricoAtendimentoModel[]) =>
      checked ? selectPage(historico) : removeSelectionPage(page)

    const imprimirRegistrosHistorico = (input: ImpressaoRegistrosHistoricoInput) =>
      handleImpressaoPath({
        apiPath: api.registrosHistorico.imprimirRegistrosHistorico,
        values: input,
        file: `HistoricoClinico.pdf`,
        dateFormat: 'yyyyMMdd_HHmm',
      })

    const print = async () => {
      try {
        const res = await imprimirRegistrosHistorico({ registros: Object.values(selectionPages).flat() })
        clearSelections()
        return res
      } catch (e) {
        return handleImpressaoError(e as any)
      }
    }

    return {
      countSelectedInPage: selectionPages[page]?.length ?? 0,
      isAllPageItemsSelected,
      isItemSelected,
      toggleSelectItem: handleSelectItem,
      toggleSelectPage: handleSelectPage,
      clearPageSelection: () => removeSelectionPage(page),
      reset: clearSelections,
      remove,
      print,
    }
  }, [
    selectionPages,
    page,
    enabledToPrintItemCount,
    clearSelections,
    setSelectionPage,
    removeSelectionPage,
    handleImpressaoPath,
    handleImpressaoError,
  ])
}
